<template>
  <form
    action
    autocomplete="off"
    id="loginform"
    ref="loginform"
    @submit.prevent="login"
  >
    <template v-if="!tfaNeeded">
      <div v-show="showBankId" id="bank-id">
        <bank-id
          :organization-id="organizationId"
          :group-id="groupId"
          :redirect="redirect"
        ></bank-id>
      </div>

      <div v-show="showUsername" class="col s12" id="user-pass">
        <div class="col s12">
          <s-text-input
            id="InputLoginUsername"
            data-cy="login-form-personnummer"
            autocomplete="off"
            autocapitalize="none"
            autocorrect="off"
            :rounded="true"
            spellcheck="false"
            type="tel"
            pattern="/^(19|20)?(\d{6}([-+]|\s)\d{4}|(?!19|20)\d{10})$/"
            :label="$t('generic.personnummer')"
            v-model="loginDetails.username"
            @keyup.enter="login"
            @keyup="el => this.fixPersonnummer(el.target.value)"
          ></s-text-input>
        </div>
        <div class="col s12" style="margin-top: 40px">
          <s-text-input
            type="password"
            id="InputLoginPassword"
            data-cy="login-form-password"
            @keyup.enter="login"
            @keyup="error = null"
            :label="$t('generic.password')"
            autocapitalize="none"
            autocomplete="off"
            :rounded="true"
            v-model="loginDetails.password"
          ></s-text-input>
          <flex-grid
            justify="space-between"
            :force-row="true"
            v-if="!hideExtras"
          >
            <span class="ml20 errors" :data-cy="errorid" v-text="error"></span>
            <div style="text-align: right">
              <router-link
                class="mr10 underline"
                data-cy="login-link-reset_password"
                style="display: block"
                :to="{ name: 'reset-password' }"
                >{{ $t('views.login.forgotPassword') }}</router-link
              >
            </div>
          </flex-grid>
        </div>

        <div class="button-container">
          <s-button
            id="ButtonLogin"
            element-name="login-form-loginbutton"
            :loading="loading"
            :small="true"
            color="blue"
            :disabled="!canSubmitLogin"
            @click="login"
            >{{ $t('generic.login') }}</s-button
          >
        </div>
      </div>
    </template>

    <template v-if="tfaNeeded">
      <div class="page-sub-header">
        <p>{{ $tc('views.login.accountSecuredWith2FA') }}</p>
        <p v-if="!useBackupCode">
          {{ $tc('views.login.typeInGoogleCode') }}
          <img src="/icons/google-authenticator.png" width="16" /> Google
          Authenticator.
        </p>
        <p v-else>{{ $tc('views.login.typeInBackupCode') }}</p>
        <t-f-a-auth
          v-if="!useBackupCode"
          :tfa-code="tfaCode"
          @submit="authenticateTFA()"
        ></t-f-a-auth>

        <s-text-input
          v-if="useBackupCode"
          v-model="backupCode"
          maxlength="12"
        ></s-text-input>
        <div class="col s12 mt20 backup-code-link mb0">
          <a @click="toggleUseBackupCode" class="pointer">{{
            useBackupCode ? $t('generic.back') : $t('views.login.useBackupCode')
          }}</a>
        </div>
      </div>

      <flex-grid :force-row="true" class="button-container mt20">
        <s-button
          :small="true"
          :dark="true"
          :flat="true"
          class="underline"
          :to="{ name: 'start' }"
          >{{ $t('generic.cancel') }}</s-button
        >
        <s-button
          id="ButtonLogin"
          :loading="loading"
          color="blue"
          :small="true"
          @click="authenticateTFA()"
          :disabled="!canSubmitBackupCode"
          >{{ $t('generic.authenticate') }}</s-button
        >
      </flex-grid>
      <span class="mt20 tfaerrors" :data-cy="errorid" v-text="error"></span>
    </template>
  </form>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { AUTH_ACTIONS, AUTH_GETTERS } from '@/store/modules/auth';
import { CURRENT_USER_ACTIONS } from '@/store/modules/currentuser';
import TFAAuth from '@common/Components/Auth/TFAAuth.vue';
import FormSetup from '@common/Mixins/FormSetup';
import FlexGrid from '@common/Components/Grids/FlexGrid.vue';
import BankId from '@common/Components/Auth/Bankid.vue';
import { fixPersonnummer } from '@common/Helpers/strings/';

export default {
  components: {
    TFAAuth,
    FlexGrid,
    BankId
  },
  props: {
    redirect: String,
    hideExtras: Boolean,
    organizationId: {
      type: String,
      default: '0'
    },
    groupId: {
      type: String,
      default: '0'
    },
    callback: {
      type: Function,
      required: false
    },
    showBankId: {
      type: Boolean,
      default: true
    },
    showUsername: {
      type: Boolean,
      default: true
    }
  },
  mixins: [FormSetup],
  data() {
    return {
      loginDetails: {
        username: '',
        password: '',
        roleId: 'pat',
        remember: true
      },
      loading: false,
      error: '',
      errorid: '',
      tfaNeeded: false,
      tfaCode: Array.from(new Array(6)),
      useBackupCode: false,
      backupCode: ''
    };
  },
  mounted() {
    this.$_FormSetup_setupForm(100);
    M.AutoInit();
    if (this.redirect && this.$route.query?.redirect !== this.redirect) {
      this.$router.push({ query: { redirect: this.redirect } });
    }
  },
  methods: {
    ...mapActions('auth', {
      $login: AUTH_ACTIONS.LOGIN
    }),
    ...mapActions('currentuser', {
      $auth2fa: CURRENT_USER_ACTIONS.AUTHENTICATE_2FA,
      $auth2faBackupCode: CURRENT_USER_ACTIONS.AUTHENTICATE_2FA_BACKUP_CODE
    }),
    toggleUseBackupCode() {
      this.useBackupCode = !this.useBackupCode;

      this.error = '';
    },
    login() {
      this.loading = true;
      this.loginDetails = {
        ...this.loginDetails,
        organizationId: this.organizationId,
        groupId: this.groupId
      };
      this.$login(this.loginDetails)
        .then(response => {
          if (this.$token.twofactor_enabled === 1) {
            this.tfaNeeded = true;
          } else {
            if (this.callback) {
              return this.callback(response);
            }

            const redirect = this.$route.query?.redirect || '/';
            this.$router.push({ path: redirect }).catch(e => {});
          }
        })
        .catch(error => {
          console.log(error);
          if (error.error_description) {
            this.errorid = `login-error-${error.error}`;
            this.error = this.$root.$t(`errors.${error.error}`);
          } else {
            this.errorid = 'login-error-noresponse';
            this.error = this.$root.$t('errors.noResponse');
          }
        })
        .finally(() => (this.loading = false));
    },
    authenticateTFA() {
      this.loading = true;

      const data = {};
      let authCall = null;

      if (this.useBackupCode) {
        data.BackupCode = this.backupCode;
        authCall = this.$auth2faBackupCode;
      } else {
        data.Code = this.tfaCode.join('');
        authCall = this.$auth2fa;
      }

      data.SessionId = this.$sessionId;

      authCall(data)
        .then(() => this.$router.push({ path: '/' }))
        .catch(error => {
          if (error.Message) {
            this.errorid = 'login-error-invalid_2fa_code';
            this.error = this.$root.$t('errors.invalid_2fa_code');
          } else if (error.error_description) {
            this.errorid = `login-error-${error.error}`;
            this.error = this.$root.$t(`errors.${error.error}`);
          } else {
            this.errorid = 'login-error-noresponse';
            this.error = this.$root.$t('errors.noResponse');
          }
        })
        .finally(() => (this.loading = false));
    },
    fixPersonnummer(pnr) {
      if (pnr.match(/•{4}/)) {
        return;
      }
      const _fixPersonnummer = fixPersonnummer(pnr);
      this.loginDetails.username = _fixPersonnummer;
    }
  },

  computed: {
    ...mapState({
      $status: state => state.auth.lastLogin
    }),
    ...mapGetters('auth', {
      $token: AUTH_GETTERS.TOKEN,
      $sessionId: AUTH_GETTERS.SESSION_ID
    }),
    canSubmitBackupCode() {
      if (this.useBackupCode) {
        return this.backupCode.length === 12;
      }
      return true;
    },
    canSubmitLogin() {
      return (
        this.loginDetails.username.length > 0 &&
        this.loginDetails.password.length > 0
      );
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@sass/_variables.scss';
.errors {
  color: $symptoms-red;
  display: block;
  max-width: 48%;
}
.tfaerrors {
  color: $symptoms-red;
  display: block;
  text-align: center;
}
.flex-grid {
  padding-left: 0;
  margin-top: -16px;
}
.backup-code-link {
  font-size: 0.7em;
}
.tabs {
  background: $background-color;
  .tab a img {
    margin-top: 8px;
  }
}
</style>
